<template>
  <v-container fluid class="personal-card">
    <v-alert v-if="errorMessage" type="error" class="mb-4" closable>
      {{ errorMessage.message || "Не удалось загрузить данные студента" }}
    </v-alert>

    <v-card flat v-if="!hasError && dataLoaded">
      <v-card-title class="d-flex align-center">
        <v-menu>
          <template #activator="{ props }">
            <v-btn icon="mdi-dots-vertical" variant="text" v-bind="props" />
          </template>

          <v-list v-if="student.id" density="compact">
            <v-list-item
              prepend-icon="mdi-file-document-outline"
              @click="openDocumentsList"
            >
              <v-list-item-title>Опись документов</v-list-item-title>
            </v-list-item>

            <v-list-item
              prepend-icon="mdi-account-plus-outline"
              @click="createLdapRecord"
            >
              <v-list-item-title>Создать запись LDAP</v-list-item-title>
            </v-list-item>

            <v-list-item
              prepend-icon="mdi-account-convert-outline"
              @click="restoreRecord"
            >
              <v-list-item-title>Восстановить запись</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-avatar size="64" color="blue-darken-3" class="me-4">
          <span class="text-h6 text-white">{{
            student.initials || "Нов"
          }}</span>
        </v-avatar>

        <div class="d-flex align-center flex-wrap">
          <span class="text-h6 text-wrap py-2 me-4">
            {{ student.id ? student.fullName : "Новое личное дело" }}
          </span>

          <v-btn
            v-if="hasUnsavedChanges"
            color="primary"
            size="small"
            :loading="isSaving"
            :disabled="isSaving"
            @click="saveStudentData"
            class="me-2"
          >
            <v-icon start>mdi-content-save</v-icon>
            {{ student.id ? "Сохранить" : "Создать" }}
          </v-btn>

          <!-- Индикатор наличия изменений -->
          <v-chip
            v-if="hasUnsavedChanges && !isSaving"
            size="small"
            color="warning"
            variant="tonal"
            class="me-2"
          >
            <v-icon start size="16">mdi-pencil</v-icon>
            {{ student.id ? "Есть изменения" : "Заполняется" }}
          </v-chip>
        </div>

        <v-spacer />
      </v-card-title>

      <v-tabs class="pro-tabs" v-model="activeTab" style="height: auto">
        <v-tab v-for="tab in tabs" :key="tab.value" :value="tab.value">
          {{ tab.title }}
        </v-tab>
      </v-tabs>

      <v-tabs-window v-model="activeTab">
        <v-tabs-window-item
          v-for="tab in tabs"
          :key="tab.value"
          :value="tab.value"
        >
          <v-card flat tile>
            <v-card-text>
              <keep-alive>
                <component
                  :is="tab.component"
                  :student="student"
                  @update:student="onStudentUpdated"
                  @update:parents="onParentsUpdated"
                />
              </keep-alive>
            </v-card-text>
          </v-card>
        </v-tabs-window-item>
      </v-tabs-window>
    </v-card>

    <v-card flat v-else-if="!hasError && !dataLoaded">
      <v-skeleton-loader type="card" />
    </v-card>
  </v-container>
</template>

<script>
import { markRaw } from "vue";

import contactDetails from "./contactDetails.vue";
import educationData from "./educationData.vue";
import personalData from "./personalData.vue";
import personalParents from "./personalParents.vue";
import personalDocuments from "./personalDocuments.vue";
import additionalInfo from "./personalAdditionalInfo.vue";

const TABS_CONFIG = [
  {
    title: "Основная информация",
    value: "tab1",
    component: markRaw(personalData),
  },
  {
    title: "Контактная информация",
    value: "tab2",
    component: markRaw(contactDetails),
  },
  {
    title: "Родители",
    value: "tab3",
    component: markRaw(personalParents),
  },
  {
    title: "Обучение",
    value: "tab4",
    component: markRaw(educationData),
  },
  {
    title: "Документы",
    value: "tab5",
    component: markRaw(personalDocuments),
  },
  {
    title: "Дополнительно",
    value: "tab6",
    component: markRaw(additionalInfo),
  },
];

export default {
  name: "StudentCard",

  components: {
    contactDetails,
    personalData,
    personalParents,
    educationData,
    personalDocuments,
    additionalInfo,
  },

  data() {
    return {
      student: {
        id: null,
        fullName: "",
        initials: "",
        firstname: "",
        lastname: "",
        patronymic: "",
        status: 0,
        birthday: "",
        gender: 0,
        hostel: 0,
        phone: "",
        email: "",
        user_id: 0,
        login: "",
        isbudget: null,
        quota: false,
        in_group: false,
        study: "",
        specialty: "",
        group: "",
        level: null,
        birthplace: null,
        notes: null,
        target: null,
        start_year: null,
        social_status: null,
        diplom_number: null,
        diplom_date: null,
        phone1: null,
        work: null,
        position: null,
        experience: null,
        live_addr_id: null,
        reg_addr_id: null,
        diplom_series: null,
        passport: {
          number: null,
          series: null,
          issuedBy: null,
          issueDate: null,
          subdivisionCode: null,
          documentType: null,
        },
        diplom_reg_number: null,
        diplom_status: null,
        diplom_type: null,
        snils: null,
        health_id: null,
        ph_group: 0,
        c_type: null,
        country_type: null,
        military_office_id: 0,
        military_office: null,
        grants: [],
        parents: [],
        privileges: [],
      },
      activeTab: "tab1",
      errorMessage: null,
      hasError: false,
      dataLoaded: false,
      tabs: TABS_CONFIG,
      originalStudent: null,
      isSaving: false,
    };
  },

  computed: {
    hasUnsavedChanges() {
      if (!this.originalStudent || !this.student) return false;
      return (
        JSON.stringify(this.originalStudent) !== JSON.stringify(this.student)
      );
    },
  },

  created() {
    const personalId = this.$route.params.personal_id;

    if (personalId) {
      this.loadStudent();
      return;
    }
    this.initNewStudent();
  },

  methods: {
    initNewStudent() {
      this.originalStudent = JSON.parse(JSON.stringify(this.student));
      this.dataLoaded = true;
    },

    loadStudent() {
      const personalId = this.$route.params.personal_id;
      this.hasError = false;
      this.errorMessage = null;
      this.dataLoaded = false;

      if (!personalId) {
        this.initNewStudent();
        return;
      }

      this.axios
        .post(
          `${this.$model.wwwroot}/procollege/manage_groups/secretariat/studentCard/getStudent`,
          new URLSearchParams({
            personal_id: personalId,
          })
        )
        .then((res) => {
          if (res.data) {
            this.student = { ...res.data };
            if (this.student.birthday) {
              const parts = this.student.birthday.split(".");
              if (parts.length === 3) {
                const [day, month, year] = parts;
                this.student.birthday = `${year}-${month.padStart(
                  2,
                  "0"
                )}-${day.padStart(2, "0")}`;
              }
            }
            this.originalStudent = JSON.parse(JSON.stringify(this.student));
          }
        })
        .catch((error) => {
          this.hasError = true;
          this.handleError(error);
          console.error("Ошибка загрузки студента:", error);
        })
        .finally(() => {
          this.dataLoaded = true;
        });
    },

    async saveStudentData() {
      if (this.isSaving) return;

      this.isSaving = true;

      try {
        const dataToSend = { ...this.student };

        // Находим измененные поля
        const changedFields = [];
        for (const key in dataToSend) {
          if (
            JSON.stringify(this.originalStudent[key]) !==
            JSON.stringify(dataToSend[key])
          ) {
            changedFields.push(key);
          }
        }

        // Преобразование даты рождения
        if (dataToSend.birthday) {
          const date = new Date(dataToSend.birthday);
          dataToSend.birthday = date.toLocaleDateString("ru-RU", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        }

        // Очищаем пустые значения
        if (dataToSend.target === 0 || dataToSend.target === null) {
          delete dataToSend.target;
        }

        // Отправляем на единый метод сохранения
        const response = await this.axios.post(
          `${this.$model.wwwroot}/procollege/manage_groups/secretariat/studentCard/saveStudent`,
          new URLSearchParams({
            student: JSON.stringify(dataToSend),
            personal_id: this.student.id || null,
            changed_fields: JSON.stringify(changedFields),
          })
        );

        // Преобразуем ответ в объект
        const responseData =
          typeof response.data === "string"
            ? JSON.parse(response.data)
            : response.data;

        // Обработка ответа
        if (responseData?.success) {
          const isNew = !this.student.id;

          // Обновляем ID если создавали нового
          if (isNew && responseData.id) {
            this.student.id = responseData.id;
            // Обновляем URL
            this.$router.replace({
              params: { personal_id: responseData.id },
            });
          }

          // Обновляем оригинальные данные
          this.originalStudent = JSON.parse(JSON.stringify(this.student));

          // Показываем сообщение
          this.$toast?.success(
            responseData.message ||
              (isNew ? "Личное дело создано" : "Данные сохранены")
          );

          // Эмитим событие для родительского компонента
          this.$emit("saved", {
            student: this.student,
            isNew: isNew,
          });
        } else {
          // Если success === false
          throw new Error(responseData?.message || "Ошибка сохранения");
        }
      } catch (error) {
        this.handleError(error);
        this.$toast?.error("Ошибка сохранения");
        console.error("Ошибка сохранения:", error);
      } finally {
        this.isSaving = false;
      }
    },

    handleError(error) {
      if (error.response?.data) {
        this.errorMessage = { message: error.response.data };
      } else if (error.message) {
        this.errorMessage = { message: error.message };
      } else {
        this.errorMessage = { message: "Ошибка соединения с сервером" };
      }
      console.error("Ошибка:", error);
    },

    onStudentUpdated(updatedData) {
      this.student = { ...this.student, ...updatedData };
    },

    onParentsUpdated(parents) {
      this.student.parents = parents;
    },

    openDocumentsList() {
      if (!this.student.id) {
        this.$toast?.warning("Сначала сохраните личное дело");
        return;
      }
      console.log("Открыть опись документов");
    },

    createLdapRecord() {
      if (!this.student.id) {
        this.$toast?.warning("Сначала сохраните личное дело");
        return;
      }
      console.log("Создать запись LDAP");
    },

    restoreRecord() {
      if (!this.student.id) {
        this.$toast?.warning("Сначала сохраните личное дело");
        return;
      }
      console.log("Восстановить запись");
    },
  },
};
</script>

<style>
.form-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.form-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  max-width: 800px;
  width: 100%;
}

.form-row :deep(.v-input) {
  flex: 1;
}

.no-margin ::v-deep(label) {
  margin-top: 0 !important;
}

.form-label {
  text-align: right;
  width: 160px;
  font-size: 14px;
  color: rgb(71, 71, 71);
  flex-shrink: 0;
}

.address-details {
  flex: 1;
}

fieldset {
  width: 100%;
  padding: 12px;
  margin: 0;
  border-radius: 8px;
  border: 1px solid #ccc;
}

legend {
  float: none;
  width: 40%;
  padding-left: 8px;
  margin-bottom: 0.5rem;
  font-size: 14px !important;
  color: rgb(71, 71, 71);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

@media (max-width: 900px) {
  .form-wrapper {
    flex-direction: column;
  }

  .form-row {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }

  .form-label {
    width: auto;
    font-weight: 500;
    text-align: left;
  }

  .address-details {
    margin-left: 0;
  }
}
</style>